import type { Company, User, UserContext, CompanyBranch, Role, Settings, Agency, Campus, Student } from '@/api';
import { permissions } from '@/common';
import { storeToRefs } from 'pinia';

/**
 * Get the company context of the current user.
 */
export const useCompany = (): {
    isDebug: Readonly<Ref<boolean>>;
    context: Readonly<Ref<UserContext | undefined>>;
    selectedStudentId: Ref<number | undefined>;
    selectedStudent: Readonly<Ref<Student | undefined>>;
    settings: Readonly<Ref<Settings | undefined>>;
    permissions: Readonly<Ref<Partial<Record<keyof typeof permissions.AllPermissions, boolean>>>>;
    user: Readonly<Ref<User | undefined>>;
    /** The user whose students and handler work this user does: the agent for an Assistant, else the user. */
    agentId: Readonly<Ref<number | undefined>>;
    company: Readonly<Ref<Company | undefined>>;
    myBranch: Readonly<Ref<CompanyBranch | undefined>>;
    authorized_branches: Readonly<Ref<Array<CompanyBranch> | undefined>>;
    agencies: Readonly<Ref<Array<Agency> | undefined>>;
    campuses: Readonly<Ref<Array<Campus> | undefined>>;
    role: Readonly<Ref<Role | undefined>>;
    isSchool: Readonly<Ref<boolean>>;
    isAgency: Readonly<Ref<boolean>>;
    isStaff: Readonly<Ref<boolean>>;
    isStudent: Readonly<Ref<boolean>>;
    isSuperAdmin: Readonly<Ref<boolean>>;
    isSubAgent: Readonly<Ref<boolean>>;
    refreshContext: () => void;
    authority: {
        overCompany: (id: Partial<Pick<Company, 'id'>>) => boolean;
        overBranch: (branch: Partial<Pick<CompanyBranch, 'id' | 'company_id'>>) => boolean;
        overUser: (user: Partial<Pick<User, 'id' | 'company_id' | 'role'>>) => boolean;
    };
    updateCompany: (company?: Partial<Company>) => void;
    updateContext: (ctx: Partial<UserContext>) => void;
    updateUser: (user?: Partial<User>) => void;
    noun: Readonly<Ref<string>>;
} => {
    const clientStore = useClientStore();
    const { context, selectedStudentId } = storeToRefs(clientStore);
    const overBranch = ({ id, company_id }: Partial<Pick<CompanyBranch, 'id' | 'company_id'>>) => {
        if (id == null) return false;
        if (company_id != null && context.value?.company.id !== company_id) {
            return context.value?.managed_company_ids?.includes(id) ?? false;
        }
        return context.value?.authorized_branches?.some(cb => cb.id === id) ?? false;
    };
    return {
        context,
        isDebug: computed(() => context.value?.app_env === 'local'),
        refreshContext: () => clientStore.refreshContext(),
        selectedStudentId,
        selectedStudent: computed(() => context.value?.students?.find(s => s.id === selectedStudentId.value)),
        settings: computed(() => context.value?.settings),
        user: computed(() => context.value?.user),
        agentId: computed(() => context.value?.user.assists_user_id ?? context.value?.user.id),
        company: computed(() => context.value?.company),
        authorized_branches: computed(() => context.value?.authorized_branches),
        agencies: computed(() => context.value?.agencies),
        campuses: computed(() => context.value?.campuses),
        myBranch: computed(() =>
            context.value?.authorized_branches?.find(cb => cb.id === context.value?.user.company_branch_id),
        ),
        role: computed(() => context.value?.role),
        isSchool: computed(() => context.value?.company.company_type === 'school'),
        isAgency: computed(() => context.value?.company.company_type === 'agency'),
        isStaff: computed(() => context.value?.company.company_type === 'staff'),
        isStudent: computed(() => context.value?.company.company_type === 'student'),
        permissions: computed(() =>
            Object.entries(permissions.AllPermissions).reduce<
                Partial<Record<keyof typeof permissions.AllPermissions, boolean>>
            >((accum, [label, permission]) => {
                accum[label as keyof typeof permissions.AllPermissions] =
                    context.value?.role.permissions.some(p => p.code === permission) ?? false;
                return accum;
            }, {}),
        ),
        isSuperAdmin: computed(() => context.value?.role?.name === 'superadmin'),
        isSubAgent: computed(
            () =>
                context.value?.company?.company_type === 'agency' &&
                context.value?.user?.parent_user_id != null &&
                context.value?.role?.name === 'sub_agent',
        ),
        authority: {
            overCompany: ({ id }: Partial<Pick<Company, 'id'>>) => {
                if (id == null) return false;
                return (context.value?.company.id === id || context.value?.managed_company_ids?.includes(id)) ?? false;
            },
            overBranch,
            overUser: ({
                id,
                company_id,
                company_branch_id,
                role,
            }: Partial<Pick<User, 'id' | 'company_branch_id' | 'company_id' | 'role'>>) => {
                if (id == null) return false;
                const rank = context.value?.role?.rank;
                return (
                    rank != null &&
                    role != null &&
                    rank > role.rank &&
                    overBranch({ id: company_branch_id, company_id })
                );
            },
        },
        updateCompany: (company?: Partial<Company>) => {
            if (context.value == null) {
                return;
            }
            context.value = {
                ...context.value,
                company: { ...context.value.company, ...company },
            };
        },
        updateContext: (ctx: Partial<UserContext>) => {
            if (context.value == null) {
                return;
            }
            context.value = {
                ...context.value,
                ...ctx,
            };
        },
        updateUser: (user?: Partial<User>) => {
            if (context.value == null) {
                return;
            }
            context.value = {
                ...context.value,
                user: { ...context.value.user, ...user },
            };
        },
        noun: computed(() => {
            if (context.value?.company?.company_type === 'school') {
                return 'Institution';
            }
            if (context.value?.company?.company_type === 'agency') {
                return 'Company';
            }
            if (context.value?.company?.company_type === 'staff') {
                return 'Staff';
            }
            if (context.value?.user?.account_type === 'student') {
                return 'Student';
            }
            return 'Company';
        }),
    };
};
